import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, Container, ListGroup, Modal, Spinner } from 'react-bootstrap';
import {
  FaCog,
  FaFileAlt,
  FaGift,
  FaSignOutAlt,
  FaTrashAlt,
  FaQuestionCircle,
  FaUser,
  FaWallet,
  FaShieldAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../assets/css/Drivers/DriverSetting/DriverSetting.css';
import DriverMasterLayout from '../../../layouts/Drivers/DriverMasterLayout';
import AppContext from '../../../context/AppContext';

const DriverSetting = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);

  const [driverInfo, setDriverInfo] = useState({
    name: "",
    email: "",
    walletBalance: 0,
    profileImage: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Step 1: Load whatever was saved at login time (fast, no flicker)
    const storedDriver = localStorage.getItem("driver");
    if (storedDriver) {
      try {
        const parsed = JSON.parse(storedDriver);
        setDriverInfo({
          name: parsed.name || parsed.drivername || parsed.fullname || "",
          email: parsed.email || "",
          walletBalance: parsed.walletBalance ?? parsed.wallet ?? 0,
          profileImage: parsed.profileImage || parsed.profile_pic || null,
        });
      } catch (e) {
        console.log("Could not parse stored driver:", e);
      }
    }

    // Step 2: Fetch fresh/live data from backend (source of truth for wallet)
    const token = authToken || localStorage.getItem("authToken");
    axios
      .post(
        `${baseUrl}/driver/profile/viewProfile`,
        {}, // body — empty since no data needs to be sent
        {
          headers: { Authorization: `Bearer ${token}` }, // headers go in config (3rd arg)
        }
      )
      .then((response) => {
        if (response.data.success) {
          const d = response.data.driver || response.data.tddriver;
          if (d) {
            setDriverInfo({
              name: d.name || d.drivername || d.fullname || "",
              email: d.email || "",
              walletBalance: d.walletBalance ?? d.wallet ?? 0,
              profileImage: d.profileImage || d.profile_pic || null,
            });
            localStorage.setItem("driver", JSON.stringify(d));
          }
        }
      })
      .catch((error) => {
        console.log("Driver profile fetch error:", error);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  // Handle delete account confirmation
  const handleDeleteAccount = () => {
    setShowDeleteModal(false);
    console.log('Deleting account...');

    navigate('/driver-login');
  };

  // Handle logout confirmation
  const handleLogout = () => {
    setShowLogoutModal(false);
    localStorage.removeItem("authToken");
    localStorage.removeItem("actorType");
    localStorage.removeItem("driver");
    navigate('/driver-login');
  };

  // Handle menu item clicks with navigation
  const handleMenuClick = (label) => {
    switch (label) {
      case 'Profile settings':
        navigate('/driver-profile-setting');
        break;
      case 'My wallet':
        navigate('/driver-mywallet');
        break;
      case 'Offer list':
        navigate('/driver-offerlist');
        break;
      case 'App setting':
        navigate('/driver-notification');
        break;
      case 'Documents registration':
        navigate('/driver-document-registration');
        break;
      case 'Help Center':
        navigate('/driver-help-center');
        break;
        case 'Security':
    navigate('/driver-security');
      break;
      case 'Delete account':
        setShowDeleteModal(true);
        break;
      case 'Logout':
        setShowLogoutModal(true);
        break;
      default:
        console.log('Menu item clicked:', label);
    }
  };

  // Icon component for consistent styling
  const Icon = ({ children, color = '#1B9B82' }) => (
    <span style={{ color, fontSize: '20px', display: 'flex', alignItems: 'center' }}>
      {children}
    </span>
  );

  const menuItems = [
    {
      section: 'General',
      items: [
        { icon: <FaUser size={18} />, label: 'Profile settings', hasArrow: true, isReactIcon: true },
        { icon: <FaWallet size={18} />, label: 'My wallet', hasArrow: true, isReactIcon: true },
        { icon: <FaGift size={18} />, label: 'Offer list', hasArrow: true, isReactIcon: true },
      ]
    },
    {
      section: 'App Details',
      items: [
        { icon: <FaCog size={18} />, label: 'App setting', hasArrow: true, isReactIcon: true },
         { icon: <FaShieldAlt size={18} />, label: 'Security', hasArrow: true, isReactIcon: true },
        { icon: <FaQuestionCircle size={18} />, label: 'Help Center', hasArrow: true, isReactIcon: true },
      ]
    },
    {
      section: 'Registration Details',
      items: [
        { icon: <FaFileAlt size={18} />, label: 'Documents registration', hasArrow: true, isReactIcon: true }
      ]
    },
    {
      section: 'Alert Zone',
      sectionClass: 'alert-zone',
      items: [
        { icon: <FaTrashAlt size={18} />, label: 'Delete account', hasArrow: true, itemClass: 'alert-zone', isReactIcon: true },
        { icon: <FaSignOutAlt size={18} />, label: 'Logout', hasArrow: true, itemClass: 'alert-zone', isReactIcon: true }
      ]
    }
  ];

  return (
    <DriverMasterLayout screenName={"Settings"} centered={true}>
      <div className="driver-settings">
        <Container className="py-4">
          {/* Profile Card */}
          <Card className="driver-settings__profile-card">
            <Card.Body>
              <div className="driver-settings__profile-content">
                <div className="driver-settings__profile-avatar">
                  {driverInfo.profileImage ? (
                    <img src={driverInfo.profileImage} alt={driverInfo.name} />
                  ) : (
                    <FaUser size={24} />
                  )}
                </div>
                <div className="driver-settings__profile-info">
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <h2 className="driver-settings__profile-name">{driverInfo.name || "Driver"}</h2>
                      <p className="driver-settings__profile-email">{driverInfo.email}</p>
                    </>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Wallet Balance Card */}
          <Card className="driver-settings__wallet-card border-0 shadow-sm mb-4">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center text-white">
                <div>
                  <small className="d-block mb-1">My Wallet Balance</small>
                  <h4 className="mb-0 fw-bold">
                    {loading ? "..." : `Rs${Number(driverInfo.walletBalance).toFixed(2)}`}
                  </h4>
                </div>
                <span className="driver-settings__wallet-icon">💳</span>
              </div>
            </Card.Body>
          </Card>

          {/* Menu Sections */}
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-4">
              <h6 className="fw-bold mb-3">{section.section}</h6>
              <Card className="border-0 shadow-sm">
                <ListGroup variant="flush">
                  {section.items.map((item, itemIndex) => (
                    <ListGroup.Item
                      key={itemIndex}
                      className={`driver-settings-menu-item ${item.itemClass ? `driver-settings-${item.itemClass}-item` : ''} border-0 py-3 px-3`}
                      style={{
                        cursor: 'pointer',
                        borderBottom: itemIndex !== section.items.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}
                      onClick={() => handleMenuClick(item.label)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div className={`driver-settings-icon-wrapper me-3 ${item.itemClass ? `driver-settings-${item.itemClass}-icon` : ''}`}>
                            {item.isReactIcon ? item.icon : <Icon>{item.icon}</Icon>}
                          </div>
                          <span className={`fw-normal ${item.itemClass ? `driver-settings-${item.itemClass}-text` : ''}`}>
                            {item.label}
                          </span>
                        </div>
                        {item.hasArrow && (
                          <span className={`driver-settings-arrow ${item.itemClass ? `driver-settings-${item.itemClass}-text` : ''}`}>›</span>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </div>
          ))}

          {/* Delete Account Modal */}
          <Modal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            centered
            className="driver-settings-modal"
          >
            <Modal.Body className="driver-settings-modal__body">
              <div className="driver-settings-modal__icon-wrapper">
                <div className="driver-settings-modal__icon driver-settings-modal__icon--delete">
                  <FaTrashAlt size={24} color="white" />
                </div>
              </div>
              <h5 className="driver-settings-modal__title">Delete Your Account</h5>
              <p className="driver-settings-modal__text">
                You will lose your data by deleting your account.
              </p>
              <div className="driver-settings-modal__buttons">
                <Button
                  className="driver-settings-modal__btn driver-settings-modal__btn--cancel"
                  onClick={() => setShowDeleteModal(false)}
                >
                  No, Keep It
                </Button>
                <Button
                  className="driver-settings-modal__btn driver-settings-modal__btn--confirm"
                  onClick={handleDeleteAccount}
                >
                  Yes, Delete
                </Button>
              </div>
            </Modal.Body>
          </Modal>

          {/* Logout Modal */}
          <Modal
            show={showLogoutModal}
            onHide={() => setShowLogoutModal(false)}
            centered
            className="driver-settings-modal"
          >
            <Modal.Body className="driver-settings-modal__body">
              <div className="driver-settings-modal__icon-wrapper">
                <div className="driver-settings-modal__icon driver-settings-modal__icon--logout">
                  <FaSignOutAlt size={24} color="white" />
                </div>
              </div>
              <h5 className="driver-settings-modal__title">Come Back Soon</h5>
              <p className="driver-settings-modal__text">
                Are you sure that you want to Logout?
              </p>
              <div className="driver-settings-modal__buttons">
                <Button
                  className="driver-settings-modal__btn driver-settings-modal__btn--cancel"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Stay, logged in
                </Button>
                <Button
                  className="driver-settings-modal__btn driver-settings-modal__btn--confirm"
                  onClick={handleLogout}
                >
                  Yes, Logout
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </DriverMasterLayout>
  );
};

export default DriverSetting;